import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
} from "@mui/material";

/* -------- Helpers -------- */
const fmt = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const fmtHM = (val) => {
  if (!val) return "—";
  if (typeof val === "string" && /^\d{2}:\d{2}$/.test(val)) {
    const [H, M] = val.split(":").map((n) => parseInt(n, 10));
    const ampm = H >= 12 ? "PM" : "AM";
    const h12 = H % 12 || 12;
    const hh = String(h12).padStart(2, "0");
    const mm = String(M).padStart(2, "0");
    return `${hh}:${mm} ${ampm}`;
  }
  const dt = new Date(val);
  if (!isNaN(dt.getTime())) {
    let h = dt.getHours();
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    const hh = String(h).padStart(2, "0");
    const mm = String(dt.getMinutes()).padStart(2, "0");
    return `${hh}:${mm} ${ampm}`;
  }
  return String(val);
};

const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v === 0) return 0;
    if (v !== null && v !== undefined) {
      if (typeof v === "string") {
        if (v.trim() !== "") return v;
      } else {
        return v;
      }
    }
  }
  return undefined;
};

const Row = ({ label, value }) => (
  <tr style={{ background: "white" }}>
    <td
      style={{
        width: "30%",
        padding: "6px",
        border: "1px solid #ddd",
        fontWeight: 600,
      }}
    >
      {label}
    </td>
    <td
      style={{
        width: "70%",
        padding: "6px",
        border: "1px solid #ddd",
        whiteSpace: "pre-wrap",
      }}
    >
      {value ?? "—"}
    </td>
  </tr>
);

const Section = ({ title }) => (
  <tr>
    <td
      colSpan={2}
      style={{
        padding: "8px",
        border: "1px solid #ddd",
        fontWeight: "bold",
      }}
    >
      {title}
    </td>
  </tr>
);

/* Case-insensitive getters */
const getKeyCi = (obj, wanted) => {
  if (!obj || !wanted) return undefined;
  const lower = wanted.toLowerCase();
  const found = Object.keys(obj).find((k) => k.toLowerCase() === lower);
  return found ? obj[found] : undefined;
};
const pick = (obj, ...names) => {
  for (const n of names) {
    const v = getKeyCi(obj, n);
    if (
      v !== undefined &&
      v !== null &&
      (typeof v !== "string" || v.trim() !== "")
    )
      return v;
  }
  return undefined;
};

/* Deep JSON parsing (for stringified payloads) */
const parseJsonDeep = (input, maxDepth = 5) => {
  let v = input;
  let depth = 0;
  try {
    while (typeof v === "string" && depth < maxDepth) {
      v = JSON.parse(v);
      depth++;
    }
  } catch {
    // stop on parse errors
  }
  return v;
};

const asArray = (val) => {
  const v = parseJsonDeep(val);
  if (Array.isArray(v)) return v;
  if (v && typeof v === "object") return [v];
  return [];
};

/* Normalizer for Doula Birth Overview */
const normalizeBirth = (src) => {
  if (!src) return {};
  const root = src?.data && typeof src.data === "object" ? src.data : src;

  return {
    DOS: pick(root, "DOS", "AssessmentDate", "assessmentDate"),
    DeliveryDate: pick(root, "DeliveryDate"),
    PregnancyNumber: pick(root, "PregnancyNumber"),
    BirthNumber: pick(root, "BirthNumber"),
    DoctorOrMidwife: pick(root, "DoctorOrMidwife", "doctorOrMidwife"),
    ClientEthnicity: pick(root, "ClientEthnicity", "clientEthnicity"),
    childbirthClasses: pick(root, "childbirthClasses", "ChildbirthClasses"),
    TimeDoulaArrived: pick(root, "TimeDoulaArrived", "timeDoulaArrived"),
    TimeDoulaLeftBirth: pick(root, "TimeDoulaLeftBirth", "timeDoulaLeftBirth"),
    WhoAttended: pick(root, "WhoAttended", "whoAttended"),
    BirthLocation: pick(root, "BirthLocation", "birthLocation"),
    MethodOfBirth: pick(root, "MethodOfBirth", "methodOfBirth"),
    ListAnylaborComplications: pick(
      root,
      "ListAnylaborComplications",
      "laborComplications"
    ),
    ListAnyComplications: pick(root, "ListAnyComplications", "clientComplications"),
    ListAnyComplicationsWithbaby: pick(
      root,
      "ListAnyComplicationsWithbaby",
      "babyComplications"
    ),
    BabyName: pick(root, "BabyName", "babyName"),
    Babyweight: pick(root, "Babyweight", "babyWeight"),
    Babylength: pick(root, "Babylength", "babyLength"),
    BabyAPGAR: pick(root, "BabyAPGAR", "babyAPGAR", "babyApgar"),
    clientHoldbaby: pick(root, "clientHoldbaby", "skinToSkin"),
    clientInitiateBreastfeeding: pick(
      root,
      "clientInitiiateBreastfeeding",
      "clientInitiateBreastfeeding",
      "breastfeedingInitiated"
    ),
    issuesWiththeStaff: pick(root, "issuesWiththeStaff", "issuesWithStaff"),
    issuesWiththeFamily: pick(root, "issuesWiththeFamily", "issuesWithFamily"),
  };
};

/* ------------------------- Component ------------------------- */

const BirthOverviewPreview = ({ data, onClose }) => {
  const [form, setForm] = useState({});
  const [birth, setBirth] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const API_BASE_URL =
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "https://neuroez4.devforhealth.com/api";

  const patientFormId = data?.patientFormId;

  const baseFormUrl = `${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`;
  const birthUrl = `${API_BASE_URL}/DoulaForms/getBirthOverviewById?PatientFormId=${patientFormId}`;

  useEffect(() => {
    if (!patientFormId) return;
    let active = true;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("neuro_patientportal_auth_token");
        if (!token) throw new Error("Authentication token missing.");

        const headers = { Authorization: `Bearer ${token}` };

        // Base form (JSON)
        const formRes = await fetch(baseFormUrl, { headers });
        if (!formRes.ok)
          throw new Error(`Form load failed (${formRes.status})`);
        const formRaw = await formRes.json();
        const formData = Array.isArray(formRaw)
          ? formRaw[0] || {}
          : formRaw || {};
        if (!active) return;
        setForm(formData);

        // Birth overview (can be double-encoded)
        const bRes = await fetch(birthUrl, { headers });

        if (bRes.ok) {
          const bText = await bRes.text();
          const parsed = parseJsonDeep(bText);
          const bArr = asArray(parsed);
          const bObj = bArr[0] || {};
          if (active) setBirth(normalizeBirth(bObj));
        } else {
          if (active) setBirth({});
        }
      } catch (e) {
        if (active) setError(e.message || "Failed to load Birth Overview.");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [API_BASE_URL, patientFormId, baseFormUrl, birthUrl]);

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          height: "60vh",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Typography color="error" align="center" sx={{ p: 3 }}>
        {error}
      </Typography>
    );

  // Header values
  const fileNo = firstNonEmpty(form.accountNo) ?? "—";
  const clientName = firstNonEmpty(form.patientName, form.clientName) ?? "—";
  const dobVal = fmt(firstNonEmpty(form.dob, form.dateOfBirth));
  const dosVal = fmt(firstNonEmpty(birth.DOS, form.assessmentDate));
  const headerTitle =
    firstNonEmpty(form.customFormTitle, data?.formTitle) || "Birth Overview";

  // Signatures (text fields from form)
  const signCertifiedDoula =
    firstNonEmpty(form.messageCertifiedDoulaSignature) || "—";
  const signSupervisor = firstNonEmpty(form.messageSupervisorSignature) || "—";
  const signRN = firstNonEmpty(form.messageRnSignature) || "—";
  const signNP = firstNonEmpty(form.messageNpSignature) || "—";
  const signOBGYN =
    firstNonEmpty(form.messageObgynPhysicianSignature) || "—";

  return (
    <Paper
      sx={{
        p: 4,
        bgcolor: "#fff",
        borderRadius: 2,
        boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: "-16px",
          zIndex: (t) => t.zIndex.appBar,
          bgcolor: "#fff",
          p: { xs: 2, sm: 3, md: 3 },
          pb: 0,
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            position: "absolute",
            top: { xs: 8, sm: 10, md: 10 },
            left: { xs: 8, sm: 16 },
            fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" },
            maxWidth: { xs: "50%", sm: "40%", md: "auto" },
          }}
        >
          <strong>DOS:</strong> {dosVal}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: { xs: 24, sm: 24, md: 22 },
            left: "50%",
            transform: "translateX(-50%)",
            fontWeight: "bold",
            color: "#000",
            fontSize: { xs: "0.9rem", sm: "1.05rem", md: "1.2rem" },
            maxWidth: { xs: "90%", sm: "70%", md: "60%" },
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "center",
          }}
        >
          {headerTitle}
        </Typography>

        <Box sx={{ height: { xs: 44, sm: 46, md: 44 } }} />
        <Divider />
      </Box>

      {/* Patient strip */}
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Grid item xs={4}>
          <Typography variant="body2">
            <strong>File #:</strong> {fileNo}
          </Typography>
        </Grid>
        <Grid item xs={4} textAlign="center">
          <Typography variant="body2">
            <strong>Client Name:</strong> {clientName}
          </Typography>
        </Grid>
        <Grid item xs={4} textAlign="right">
          <Typography variant="body2">
            <strong>DOB:</strong> {dobVal}
          </Typography>
        </Grid>
      </Grid>
      <Divider sx={{ mb: 3 }} />

      {/* Body */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "12pt",
          background: "white",
        }}
      >
        <tbody>
          <Section title="Labor & Birth Details" />
          <Row label="Pregnancy Number" value={birth.PregnancyNumber || "—"} />
          <Row label="Birth Number" value={birth.BirthNumber || "—"} />
          <Row label="Doctor or Midwife" value={birth.DoctorOrMidwife || "—"} />
          <Row label="Birth Location" value={birth.BirthLocation || "—"} />
          <Row label="Method of Birth" value={birth.MethodOfBirth || "—"} />
          <Row label="Who Attended" value={birth.WhoAttended || "—"} />
          <Row
            label="Childbirth Classes"
            value={birth.childbirthClasses || "—"}
          />
          <Row label="Client Ethnicity" value={birth.ClientEthnicity || "—"} />

          <Section title="Timing" />
          <Row label="Delivery Date" value={fmt(birth.DeliveryDate)} />
          <Row
            label="Time Doula Arrived to Labor"
            value={fmtHM(birth.TimeDoulaArrived)}
          />
          <Row
            label="Time Doula Left Birth"
            value={fmtHM(birth.TimeDoulaLeftBirth)}
          />

          <Section title="Complications (First 2 Hours Postpartum)" />
          <Row
            label="Labor Complications"
            value={birth.ListAnylaborComplications || "—"}
          />
          <Row
            label="Client Complications"
            value={birth.ListAnyComplications || "—"}
          />
          <Row
            label="Baby Complications"
            value={birth.ListAnyComplicationsWithbaby || "—"}
          />

          <Section title="Baby" />
          <Row label="Baby's Name" value={birth.BabyName || "—"} />
          <Row label="Baby's Weight" value={birth.Babyweight || "—"} />
          <Row label="Baby's Length" value={birth.Babylength || "—"} />
          <Row
            label="Baby's APGAR (1 and 5 min)"
            value={birth.BabyAPGAR || "—"}
          />

          <Section title="Postpartum" />
          <Row
            label="Skin to skin in first 60 minutes"
            value={birth.clientHoldbaby || "—"}
          />
          <Row
            label="Breastfeeding initiated in first 2 hours"
            value={birth.clientInitiateBreastfeeding || "—"}
          />
          <Row label="Issues with staff" value={birth.issuesWiththeStaff || "—"} />
          <Row
            label="Issues with family/support team"
            value={birth.issuesWiththeFamily || "—"}
          />

          <Section title="Signatures" />
          {/* Client Signature UI REMOVED as requested */}
          <Row label="Certified Doula Signature" value={signCertifiedDoula} />
          <Row label="Supervisor Signature" value={signSupervisor} />
          <Row label="RN Signature" value={signRN} />
          <Row label="NP Signature" value={signNP} />
          <Row label="OBGYN Physician Signature" value={signOBGYN} />
        </tbody>
      </table>
    </Paper>
  );
};

export default BirthOverviewPreview;